import Link from 'next/link';
import { useRouter } from 'next/router';

import { ContentContainer } from 'themes/organic/components/ContentContainer';
import { Item } from 'themes/organic/components/Item/Item';
import { OrganicButton } from 'themes/organic/components/OrganicButton';
import { TexturedCircle } from 'themes/organic/components/TexturedCircle/TexturedCircle';

import { useMediaQuery } from 'glossDS/hooks/useMediaQuery';

import { Heading } from 'components/Heading';

import { useBnplPromo } from 'hooks/bnpl/useBnplPromo';

import { BuyNowPayLaterPromo } from 'routes/invoices/components/BuyNowPayLaterPromo/BuyNowPayLaterPromo';
import { PackageItem } from 'routes/packages/types';

import { getBnplUnitPriceFromStringDollars } from 'utils/bnplUtils';

import s from './OrganicPackages.module.css';

type OrganicPackagesProps = {
  packageList: PackageItem[];
};

export const OrganicPackages = ({ packageList }: OrganicPackagesProps) => {
  const { push } = useRouter();
  const { isEnabled, bnplCapabilities } = useBnplPromo();

  const wideScreen = useMediaQuery('(min-width: 1200px)');
  const alignLeft = wideScreen;
  const promoTextAlign = alignLeft ? 'left' : 'center';

  const onClick = () => {
    void push('/contact');
  };

  return (
    <section className={s.root}>
      <ContentContainer>
        <TexturedCircle className={s.texturedCircle} />
        <div className={s.content}>
          <Heading as="h1" variant="large">
            Packages
          </Heading>
          <p className={s.description}>
            <b>Already have a package?</b> Book as usual on our{' '}
            <Link href="/services">Services page</Link> — your credits will
            apply automatically at checkout.
            <br /> Coming soon, you&apos;ll be able to purchase your packages
            directly from this website.
          </p>
          <ul className={s.packageList}>
            {packageList?.map((packageItem) => {
              const bnplUnitPrice = isEnabled
                ? getBnplUnitPriceFromStringDollars(packageItem.price)
                : undefined;

              return (
                <li key={packageItem.token} className={s.packageItem}>
                  <Item
                    title={packageItem.name}
                    listView
                    button={
                      <OrganicButton className={s.button} onClick={onClick}>
                        Contact to purchase
                      </OrganicButton>
                    }
                  >
                    <p>{packageItem.description}</p>
                    <div className={s.priceContainer}>
                      <p>
                        {packageItem.price} {bnplUnitPrice ? 'or' : null}
                      </p>
                      {bnplUnitPrice ? (
                        <div className={s.bnplEligible}>
                          <BuyNowPayLaterPromo
                            unitAmount={bnplUnitPrice}
                            textAlign={promoTextAlign}
                            paymentMethodTypes={bnplCapabilities}
                          />
                        </div>
                      ) : null}
                    </div>
                    <ul className={s.items}>
                      {packageItem.items.map((item) => (
                        <li key={item.token} className={s.item}>
                          {item.quantity}x {item.name}
                        </li>
                      ))}
                    </ul>
                  </Item>
                </li>
              );
            })}
          </ul>
        </div>
      </ContentContainer>
    </section>
  );
};
